import { BarcodeRedeemErrorCode, SingleBarcodeRedeemErrorCode, Status } from './types'
import { Component } from 'react'
import checkIcon from './icons/colored/check.svg'
import warningIcon from './icons/colored/warning.svg'
import searchOffIcon from './icons/colored/search_off.svg'
import pastValidityIcon from './icons/colored/past_validity.svg'
import qrCodeIcon from './icons/colored/qr_code.svg'
import futureValidityIcon from './icons/colored/future_validity.svg'
import timeLimitReachedIcon from './icons/colored/time_limit_reached.svg'
import whiteSpinner from './icons/white/spinner.svg'
import whiteCheckIcon from './icons/white/check.svg'
import whiteWarningIcon from './icons/white/warning.svg'
import whiteSearchOffIcon from './icons/white/search_off.svg'
import whitePastValidityIcon from './icons/white/past_validity.svg'
import whiteQrCodeIcon from './icons/white/qr_code.svg'
import whiteFutureValidityIcon from './icons/white/future_validity.svg'
import whiteTimeLimitReachedIcon from './icons/white/time_limit_reached.svg'

import { Colors, DashboardTheme } from '../../theme'
import { CreateExceptionBarcodeError } from '../ordersService'
import { t } from 'translations'

export type ColorName = keyof { [Prop in keyof Colors as Colors[Prop] extends string ? Prop : never]: Colors[Prop] }

type StatusSettings = {
    color: ColorName
    bgColor?: ColorName
    historyIcon?: Component
    statusIcon: Component
    message: string
}

const statusSettingsMap: Record<Status, Omit<StatusSettings, 'message'>> = {
    initial: {
        color: 'boyBlue',
        statusIcon: whiteQrCodeIcon,
    },
    loading: {
        color: 'aluminium',
        statusIcon: whiteSpinner,
    },
    success: {
        color: 'emerald',
        historyIcon: checkIcon,
        statusIcon: whiteCheckIcon,
    },
    questionsPending: {
        color: 'aluminium',
        statusIcon: whiteQrCodeIcon,
    },
    notFound: {
        color: 'tiger',
        historyIcon: searchOffIcon,
        statusIcon: whiteSearchOffIcon,
    },
    error: {
        color: 'sunrise',
        bgColor: 'aluminium',
        historyIcon: warningIcon,
        statusIcon: whiteWarningIcon,
    },
    pastValidity: {
        color: 'sunrise',
        historyIcon: pastValidityIcon,
        statusIcon: whitePastValidityIcon,
    },
    futureValidity: {
        color: 'sun',
        historyIcon: futureValidityIcon,
        statusIcon: whiteFutureValidityIcon,
    },
    alreadyRedeemed: {
        color: 'tiger',
        historyIcon: qrCodeIcon,
        statusIcon: whiteQrCodeIcon,
    },
    alreadyScannedIn: {
        color: 'tiger',
        historyIcon: qrCodeIcon,
        statusIcon: whiteQrCodeIcon,
    },
    timeLimitReached: {
        color: 'sunrise',
        historyIcon: timeLimitReachedIcon,
        statusIcon: whiteTimeLimitReachedIcon,
    },
    notScannableCode: {
        color: 'sunrise',
        historyIcon: warningIcon,
        statusIcon: whiteWarningIcon,
    },
    scanInRequired: {
        color: 'tiger',
        historyIcon: qrCodeIcon,
        statusIcon: whiteQrCodeIcon,
    },
    barcodeNotGroupScannable: {
        color: 'sunrise',
        historyIcon: warningIcon,
        statusIcon: whiteWarningIcon,
    },
    noRedeemableBarcodesFound: {
        color: 'tiger',
        historyIcon: searchOffIcon,
        statusIcon: whiteSearchOffIcon,
    },
}

const getStatusMessage = (status: Status): string => {
    switch (status) {
        case 'initial':
            return t('orders.redeemModeDialog.status.initial')
        case 'loading':
            return t('orders.redeemModeDialog.status.loading')
        case 'success':
            return t('orders.redeemModeDialog.status.success')
        case 'questionsPending':
            return t('orders.redeemModeDialog.status.questionsPending')
        case 'notFound':
            return t('orders.redeemModeDialog.status.notFound')
        case 'error':
            return t('orders.redeemModeDialog.status.error')
        case 'pastValidity':
            return t('orders.redeemModeDialog.status.pastValidity')
        case 'futureValidity':
            return t('orders.redeemModeDialog.status.futureValidity')
        case 'alreadyRedeemed':
            return t('orders.redeemModeDialog.status.alreadyRedeemed')
        case 'alreadyScannedIn':
            return t('orders.redeemModeDialog.status.alreadyScannedIn')
        case 'timeLimitReached':
            return t('orders.redeemModeDialog.status.timeLimitReached')
        case 'notScannableCode':
            return t('orders.redeemModeDialog.status.notScannableCode')
        case 'scanInRequired':
            return t('orders.redeemModeDialog.status.scanInRequired')
        case 'barcodeNotGroupScannable':
            return t('orders.redeemModeDialog.status.barcodeNotGroupScannable')
        case 'noRedeemableBarcodesFound':
            return t('orders.redeemModeDialog.status.noRedeemableBarcodesFound')
        default:
            return t('orders.redeemModeDialog.status.error')
    }
}

export function getStatusSettings(status: Status): StatusSettings {
    return { ...(statusSettingsMap[status] || statusSettingsMap.error), message: getStatusMessage(status) }
}

export const getColorForStatus = (
    status: Status,
    theme: DashboardTheme,
    colorType: 'color' | 'bgColor' = 'color',
): string | null => {
    const colorName = statusSettingsMap[status][colorType]
    if (colorName) {
        return theme.colors[colorName]
    }
    return null
}

const statusMap: Record<BarcodeRedeemErrorCode, Status> = {
    notFound: 'notFound',
    alreadyRedeemed: 'alreadyRedeemed',
    alreadyScannedIn: 'alreadyScannedIn',
    timeLimitReached: 'timeLimitReached',
    futureValidity: 'futureValidity',
    pastValidity: 'pastValidity',
    notScannableCode: 'notScannableCode',
    scanInRequired: 'scanInRequired',

    // group scanning codes
    barcode_not_found: 'notFound',
    no_redeemable_barcodes_found: 'noRedeemableBarcodesFound',
    barcode_not_group_scannable: 'barcodeNotGroupScannable',
}

export function getStatusByErrorCode(errorCode: BarcodeRedeemErrorCode): Status {
    return statusMap[errorCode] ?? 'error'
}

export const getErrorKey = (response: CreateExceptionBarcodeError) =>
    Object.keys(response).find(
        (key) => response[key] && !['type', 'barcodes', 'error'].includes(key),
    ) as SingleBarcodeRedeemErrorCode
